import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CarFront, ShieldCheck, Sparkles } from 'lucide-react';
import { Signin } from '../../components/signin/signin';
import { useAuthStore } from '@/state/store/auth';
import { useSigninMutation } from '../../hooks/use-auth';
import { startLogin } from '../../services/sso.service';
import { SignInResponse } from '../../services/auth.service';

export const SigninPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { mutateAsync: signinMutate } = useSigninMutation<'social'>();
  const { login, setTokens } = useAuthStore();
  const isExchangingRef = useRef(false);
  const [ssoError, setSsoError] = useState<string | null>(null);

  // True only if the project has configured an OIDC client.
  const hasOidcClient = Boolean(
    (import.meta.env.VITE_BLOCKS_OIDC_CLIENT_ID as string | undefined)?.length
  );

  useEffect(() => {
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    if (code && state && !isExchangingRef.current) {
      isExchangingRef.current = true;
      (async () => {
        try {
          const res = (await signinMutate({ grantType: 'social', code, state })) as SignInResponse;
          if (res.enable_mfa) {
            navigate(`/verify-mfa?mfa_id=${res.mfaId}&mfa_type=${res.mfaType}&sso=true`, {
              replace: true,
            });
            return;
          }
          login(res.access_token ?? '', res.refresh_token ?? '');
          setTokens({ accessToken: res.access_token ?? '', refreshToken: res.refresh_token ?? '' });
          navigate('/', { replace: true });
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('SSO code exchange failed:', error);
        } finally {
          isExchangingRef.current = false;
        }
      })();
    }
  }, [searchParams, signinMutate, login, setTokens, navigate]);

  const handleSso = () => {
    setSsoError(null);
    startLogin().catch((e) => setSsoError(String(e)));
  };

  return (
    <div className="w-full">
      {/* Brand mark — replaces the Construct logo */}
      <div className="mb-8 flex items-center gap-2.5">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 shadow-lg shadow-blue-500/30">
          <CarFront className="h-5 w-5 text-white" strokeWidth={2.5} />
        </div>
        <div className="leading-tight">
          <div className="text-base font-bold tracking-tight text-high-emphasis">
            <span className="text-blue-600">Hot</span>Wheels
          </div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-medium-emphasis">Garage</div>
        </div>
      </div>

      {/* Heading */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold tracking-tight text-high-emphasis">
          {t('LOG_IN')}
        </h1>
        <p className="mt-2 text-sm text-medium-emphasis">
          {t('DONT_HAVE_ACCOUNT')}{' '}
          <Link
            to={'/signup'}
            className="font-semibold text-blue-600 hover:text-blue-700 hover:underline"
          >
            {t('SIGN_UP')}
          </Link>
        </p>
      </div>

      {/* SSO entry: only when an OIDC client is configured. */}
      {hasOidcClient && (
        <button
          type="button"
          onClick={handleSso}
          className="mb-4 flex w-full items-center justify-center gap-2 rounded-lg border border-blue-200 bg-white px-4 py-2.5 text-sm font-semibold text-blue-700 shadow-sm transition hover:bg-blue-50 hover:border-blue-300"
        >
          <Sparkles className="h-4 w-4" />
          Continue with SSO
        </button>
      )}
      {ssoError && <div className="mb-3 text-xs text-destructive">{ssoError}</div>}

      {hasOidcClient && (
        <div className="my-5 flex items-center gap-3 text-xs uppercase tracking-wider text-medium-emphasis">
          <div className="h-px flex-1 bg-zinc-200" />
          <span>or sign in with email</span>
          <div className="h-px flex-1 bg-zinc-200" />
        </div>
      )}

      <Signin />

      {/* Trust footnote */}
      <div className="mt-8 flex items-center justify-center gap-1.5 text-xs text-medium-emphasis">
        <ShieldCheck className="h-3.5 w-3.5 text-blue-600" />
        Secure session — your credentials are encrypted end-to-end.
      </div>
    </div>
  );
};
